import { Injectable } from '@angular/core';
import { Apollo, gql, MutationResult } from 'apollo-angular';
import { ApolloBase } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ApolloError, ApolloQueryResult, InMemoryCache } from '@apollo/client/core';
import { DocumentNode, FieldNode, OperationDefinitionNode } from 'graphql';
import { catchError } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, Subscription, throwError } from 'rxjs';
import { LocalStorageService } from '../../shared/storage/local-storage.service';
import { environment } from '../../../environments/environment';
import { io } from 'socket.io-client';
import { frontEndBaseUrl } from '../../shared/frontEndBaseUrl';
import { RouteInfo } from '../../shared/vertical-menu/vertical-menu.metadata';
import { PULSERIGHT_ROUTES } from '../../shared/vertical-menu/vertical-pulseright-menu-routes.config';
import { OPERATION_SERVICE_MAP, ServiceSlug, SERVICE_SLUGS, getServiceGraphQLUri } from './operation-service-map';

@Injectable({
  providedIn: 'root'
})
export class GraphQlService {
  uri = environment.apiBaseUrl;
  public userData: any = {
    auth: {},
    organizationGroupList: [],
    doctorList: [],
    SelectedOrg: '',
    SelectedOrgGrp: '',
    patientList: [],
    moduleNames: [],
    StaffList: [],
    serviceList: [],
    dashboard: {},
    managerDashboard: {},
    timeOffList: [],
    overTime: [],
  };
  menuLoader = new BehaviorSubject<string>('OrgDetails');
  private menus$ = new BehaviorSubject<any[]>([]);  // ✅ holds menu list
  menusObs$ = this.menus$.asObservable();          // components subscribe here
  refreshDoctorsList = new Subject<string>();
  booking_socket: any;
  queue_socket: any;
  queue_socket_remove: any;
  adds_socket: any;
  queueUpdateNamespace: any;
  scrollingMessageNamespace: any;
  // Support environment.socketUrl directly if the socket server is deployed on a
  // separate domain, otherwise fall back to socketPort (local) or apiBaseUrl.
  public socketEndpoint = (environment as any).socketUrl
    ? (environment as any).socketUrl
    : (environment as any).socketPort
      ? `http://localhost:${(environment as any).socketPort}`
      : `${environment.apiBaseUrl}`;
  private menus: RouteInfo[] = [];

  constructor(private apollo: Apollo, httpLink: HttpLink, private storage: LocalStorageService) {
    // The serverless backend is split across 13 independently-schema'd services,
    // each reachable at its own /<slug>/graphql path (see operation-service-map.ts).
    // Register one named Apollo client per service instead of a single default client.
    const servicePorts = (environment as any).servicePorts;
    for (const slug of SERVICE_SLUGS) {
      this.apollo.createNamed(slug, {
        link: httpLink.create({ uri: getServiceGraphQLUri(slug, this.uri, servicePorts) }),
        cache: new InMemoryCache()
      });
    }
    this.booking_socket = io(`${this.socketEndpoint}/booking`, { transports: ['websocket'] });
    this.queue_socket = io(`${this.socketEndpoint}/queue`, { transports: ['websocket'] });
    this.queue_socket_remove = io(`${this.socketEndpoint}/queueRemove`, { transports: ['websocket'] });
    this.adds_socket = io(`${this.socketEndpoint}/campaign`, { transports: ['websocket'] });
    this.queueUpdateNamespace = io(`${this.socketEndpoint}/queueUpdate`, { transports: ['websocket'] });
    this.scrollingMessageNamespace = io(`${this.socketEndpoint}/doctors`, { transports: ['websocket'] });
  }

  emitEvent(eventName: string, data: string) {
    this.booking_socket.emit(eventName, data);
  }

  /**
   * Resolves which backend service's named Apollo client should handle a
   * given GraphQL document, based on its top-level operation field name
   * (see operation-service-map.ts). Pass `serviceOverride` for operations
   * whose field name exists in more than one service (e.g. `registerDependent`).
   */
  private resolveClient(QUERY: DocumentNode, serviceOverride?: ServiceSlug): ApolloBase<any> {
    const slug = serviceOverride ?? this.getOperationSlug(QUERY);
    return this.apollo.use(slug);
  }

  private getOperationSlug(QUERY: DocumentNode): ServiceSlug {
    const operationDef = QUERY.definitions.find(
      (d): d is OperationDefinitionNode => d.kind === 'OperationDefinition'
    );
    const topLevelField = operationDef?.selectionSet.selections.find(
      (s): s is FieldNode => s.kind === 'Field'
    );
    const fieldName = topLevelField?.name.value;
    const slug = fieldName ? OPERATION_SERVICE_MAP[fieldName] : undefined;
    if (!slug) {
      throw new Error(
        `GraphQlService: no backend service mapped for GraphQL operation "${fieldName ?? 'unknown'}". ` +
        `Update operation-service-map.ts or pass an explicit serviceOverride.`
      );
    }
    return slug;
  }

  /**
   * Normalizes an ApolloError into the shape most call sites across the app
   * already expect: `error.error.errors` (an array of `{message}` objects),
   * matching the old backend's convention of returning validation errors as
   * an HTTP-level error body. Previously this service returned/threw only
   * `err.networkError`, which is `null` whenever the failure came back as a
   * standard GraphQL error (`err.graphQLErrors`) instead of an HTTP-level
   * error — and the new backend's resolvers throw plain GraphQLErrors for
   * business-rule failures far more often than the old one did, so that gap
   * silently swallowed most error messages (`error.error.errors` would throw
   * on `null.errors`). This preserves networkError-based errors as before
   * and additionally surfaces graphQLErrors/message when there's no
   * networkError, so existing `const {errors} = error.error` call sites keep
   * working either way.
   */
  private normalizeApolloError(err: ApolloError): any {
    const networkErrorBody = (err.networkError as any)?.error;
    const errors = networkErrorBody?.errors
      ?? (err.graphQLErrors?.length ? err.graphQLErrors.map(e => ({ message: e.message })) : undefined)
      ?? [{ message: err.message || 'Unknown error' }];
    return {
      networkError: err.networkError,
      graphQLErrors: err.graphQLErrors,
      message: err.message,
      error: { errors }
    };
  }


  async setMenusByAllowedList(userType: string, moduleName: string[]) {
    const menus = JSON.parse(JSON.stringify(PULSERIGHT_ROUTES));
    // Lab staff get the Diagnostics menu from their user type (the diagnostics API allows them by
    // userType), not from an organization module, so their sidebar is never left empty.
    const roleMenus = ['Lab Manager', 'Laboratorist'].includes(userType) ? ['Diagnostics'] : [];
    const result2 = ['Super Admin', 'Admin', 'Pharmacy Admin'].includes(userType) ? menus
      : menus.filter((r) => (moduleName || []).includes(r.title.trim()) || roleMenus.includes(r.title.trim()));
    const role = userType;
    const menuData = result2.filter(item => !item.hidden && item.category.includes(role)).map(item => {
      if (item.submenu && item.submenu.length > 0) {
        const filteredSubmenu = item.submenu.filter(sub => !sub.hidden && sub.category.includes(role)).map(sub => {
          if (sub.submenu && sub.submenu.length > 0) {
            sub.submenu = sub.submenu.filter(subsub => !subsub.hidden && subsub.category.includes(role));
          }
          return sub;
        });
        item.submenu = filteredSubmenu.length ? filteredSubmenu : item.submenu;
      }
      return item;
    });
    console.log('--- setMenusByAllowedList debug ---', {
      userType,
      moduleName,
      result2Titles: result2.map(r => r.title),
      finalMenuData: menuData
    });
    this.menus$.next(menuData);
    return Promise.resolve();
  }

  async loadMenus(ROUTES, userType: string) {
    const role = userType;
    const menuData = ROUTES.filter(item => !item.hidden && item.category.includes(role)).map(item => {
      if (item.submenu && item.submenu.length > 0) {
        const filteredSubmenu = item.submenu.filter(sub => !sub.hidden && sub.category.includes(role)).map(sub => {
          if (sub.submenu && sub.submenu.length > 0) {
            sub.submenu = sub.submenu.filter(subsub => !subsub.hidden && subsub.category.includes(role));
          }
          return sub;
        });
        item.submenu = filteredSubmenu.length ? filteredSubmenu : item.submenu;
      }
      return item;
    });
    return menuData;
  }

  getMenus() {
    return this.menus$.getValue(); // get current menus instantly
  }

  // emit queue Removal
  queue_emit_removeQueue(eventName: string, data: string) {
    this.queue_socket_remove.emit(eventName, data);
  }


  // emit queue position update for socket

  queue_emit_position(eventName: string, data: string) {
    this.queueUpdateNamespace.emit(eventName, data);
  }


  // emit doctor availability message

  doctor_availability_message(eventName: string, data: string) {
    this.scrollingMessageNamespace.emit(eventName, data);
  }


  // websocket Integration to perform auto refresh Queue System
  onQueuePush(): Observable<any> {
    return new Observable((observer: any) => {
      this.queue_socket?.on('message', (data) => {
        observer.next(data);
      });
    });
  }


  // Observing  queue removals
  onQueuePop(): Observable<any> {
    return new Observable((observer: any) => {
      this.queue_socket_remove?.on('message', (data: any[]) => {
        observer.next(data);
      });
    });
  }


  OnQueue_position_update(): Observable<any> {
    return new Observable((observer: any) => {
      this.queueUpdateNamespace?.on('message', (data: any[]) => {
        observer.next(data);
      });
    });
  }

  chech_doctor_availability_message(): Observable<any> {
    return new Observable((observer: any) => {
      this.scrollingMessageNamespace?.on('message', (data: any[]) => {
        observer.next(data);
      });
    });
  }

  PongqueueRemove() {
    return new Observable((observer: any) => {
      this.queue_socket_remove?.on('pong', (data: any[]) => {
        observer.next(data);
      });
    });
  }

  PongqueueAdd() {
    return new Observable((observer: any) => {
      this.queue_socket?.on('pong', (data: any[]) => {
        observer.next(data);
      });
    });
  }

  PongqueueUpdateNamespace() {
    return new Observable((observer: any) => {
      this.queueUpdateNamespace?.on('pong', (data: any[]) => {
        observer.next(data);
      });
    });
  }

  PongMessage() {
    return new Observable((observer: any) => {
      this.scrollingMessageNamespace?.on('pong', (data: any[]) => {
        observer.next(data);
      });
    });
  }

  onSlotBooked(): Observable<any> {
    return new Observable((observer: any) => {
      this.booking_socket?.on('message', (data) => {
        observer.next(data);
      });
    });
  }

  queue_emit_Event(eventName: string, data: string) {
    this.queue_socket.emit(eventName, data);
  }

  // load organizationGroup by URL
  loadOrganizationGroupURL(QUERY: any) {
    return this.resolveClient(QUERY).watchQuery({
      query: QUERY,
      variables: { url: frontEndBaseUrl },
      context: {
        headers: {
          'Content-type': 'application/json'
        }
      }
    }).valueChanges.pipe(catchError(err => throwError(err)));
  }


  // sign by email and password
  async login_Email(email: string, password: string, QUERY: any) {
    return await this.resolveClient(QUERY).mutate({
      mutation: QUERY,
      variables: { email, password },
      context: {
        headers: { 'Content-type': 'application/json' }
      }
    }).toPromise().then(async (response: MutationResult | any) => {

      // AuthPayload only exposes `user` (whose `organization` field is an array of org IDs,
      // not populated org objects) — there is no separate top-level `organization` field anymore.
      const user = response.data.login.user;
      this.userData.auth = {
        token: response.data.login.token,
        refreshToken: response.data.login.refreshToken,
        data: {
          ...user
        }
      };
      if (response.data.login.user.role === 'Super Admin') {
        await this.SelectedOrgGrpId(JSON.parse(localStorage.getItem('organizationGroup')).data._id, this.userData.auth.data.organization[0]);
        await this.getSelectedOrgId(JSON.parse(localStorage.getItem('organizationGroup')).data.organization[0]._id);
        await this.storage.saveData('pulse-right', JSON.stringify(this.userData));
      } else {
        const activeOrg = response.data.login.user.organization;
        await this.SelectedOrgGrpId(JSON.parse(localStorage.getItem('organizationGroup')).data._id, activeOrg[0]);
        await this.storage.saveData('pulse-right', JSON.stringify(this.userData));
      }
      this.menuLoader.next('OrgDetails');
      return this.userData;
    }).catch((err: ApolloError) => {
      return err.graphQLErrors;
    });
  }

  // login using phone number
  async login_Phone(number: string, kiosk: boolean, QUERY: any) {
    return await this.resolveClient(QUERY).mutate({
      mutation: QUERY,
      variables: { number, kiosk },
      context: {
        headers: { 'Content-type': 'application/json' }
      }
    }).toPromise()
      .then((response: any) => {
        // auth-service's loginWithMobile replaced the old loginPhone mutation
        return response.data.loginWithMobile;
      })
      .catch((err: ApolloError) => {
        return err.graphQLErrors;
      });
  }

  // verify login OTP
  async verifyOtpForLoginPhone(number: string, otp: string, QUERY: any) {
    return this.resolveClient(QUERY).mutate({
      mutation: QUERY,
      variables: { number, otp },
      context: {
        headers: { 'Content-type': 'application/json' }
      }
    })
      .toPromise()
      .then((response: any) => {
        // auth-service's verifyOtp replaced the old verifyOtpForLoginPhone mutation
        return response.data.verifyOtp;
      })
      .catch((err: ApolloError) => {
        throw err;
      });
  }

  login_email(email: string, password: string, QUERY: any) {
    return this.resolveClient(QUERY).mutate<MutationResult>({
      mutation: QUERY,
      variables: { email, password },
      context: {
        headers: {
          'Content-type': 'application/json',
        }
      },
    }).pipe(catchError((err: ApolloError) => throwError(this.normalizeApolloError(err))));
  }


  // fetch organization  id from localStorage
  async getSelectedOrgId(orgId) {
    this.userData.SelectedOrg = orgId;
    await this.storage.saveData('pulse-right', JSON.stringify(this.userData));
  }

  // storing the default organizationGroup Id
  async SelectedOrgGrpId(grpId, orgId) {
    this.userData.SelectedOrgGrp = grpId;
    this.userData.SelectedOrg = orgId;
    await this.storage.saveData('pulse-right', JSON.stringify(this.userData));
  }

  // fetch organization details by id
  async fetch_organization_Id(QUERY, user: any, orgId: string): Promise<any> {
    try {
      const response = await this.resolveClient(QUERY).query({
        query: QUERY,
        variables: { orgId },
        fetchPolicy: 'network-only',
        context: {
          headers: {
            'Content-type': 'application/json',
            'Authorization': 'Bearer ' + user.auth.token + ''
          }
        }
      }).toPromise();
      if (response.errors && response.errors.length > 0) {
        throw new Error(response.errors[0].message);
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  // fetch all organizationGroups

  async fetchAllOrganizationGroups(user, QUERY, input): Promise<any> {
    return await this.resolveClient(QUERY).query({
      query: QUERY,
      variables: input,
      fetchPolicy: 'network-only',
      context: {
        headers: {
          'Content-type': 'application/json',
          'Authorization': 'Bearer ' + user.auth.token + ''
        }
      }
    }).toPromise().then((response: ApolloQueryResult<any> | any) => {
      return response;
    }).catch((err: ApolloError) => {
      return { message: err.message };
    })
  }

  // fetch organization Group by Id

  async fetchOrgGrpById(user, QUERY, organizationGroupId: string): Promise<any> {
    return await this.resolveClient(QUERY).query({
      query: QUERY,
      variables: { organizationGroupId },
      fetchPolicy: 'network-only',
      context: {
        headers: {
          'Content-type': 'application/json',
          'Authorization': 'Bearer ' + user.auth.token + ''
        }
      }
    }).toPromise().then((response: ApolloQueryResult<any> | any) => {
      return response;
    }).catch((err: ApolloError | any) => {
      return { message: err.message };
    })
  }


  // dependent register
  async dependent_register(user, QUERY: any, input: any) {
    return await this.resolveClient(QUERY).mutate<any>({
      mutation: QUERY,
      variables: { input },
      context: {
        headers: {
          'Content-type': 'application/json',
          'Authorization': 'Bearer ' + user.auth.token
        }
      }
    }).toPromise().then((response: MutationResult) => {
      return response;
    }).catch((error: ApolloError) => {
      return this.normalizeApolloError(error);
    });
  }

  // register organization

  async register_organizationGRP(user, QUERY: any, input: any) {
    return await this.resolveClient(QUERY).mutate<any>({
      mutation: QUERY,
      variables: { input },
      context: {
        headers: {
          'Content-type': 'application/json',
          'Authorization': 'Bearer ' + user.auth.token
        }
      }
    }).toPromise().then((response: MutationResult) => {
      return response;
    }).catch((error: ApolloError) => {
      return this.normalizeApolloError(error);
    });
  }

  // update organization group details

  async update_organization_group_details(user: any, QUERY: any, organizationGroupId: string, updates: any[]) {
    // org-service's updateOrganizationGroup(organizationGroupId, input): OrganizationGroup
    // takes flat args, not one wrapped `input` object.
    const variables = { organizationGroupId, input: updates };
    return await this.resolveClient(QUERY).mutate<any>({
      mutation: QUERY,
      variables,
      context: {
        headers: {
          'Content-type': 'application/json',
          'Authorization': 'Bearer ' + user.auth.token
        }
      }
    }).toPromise().then((response: MutationResult) => {
      return response;
    }).catch((error: ApolloError) => {
      return this.normalizeApolloError(error);
    });
  }

  // register organization
  async register_organization(user, QUERY: any, input: any) {
    return await this.resolveClient(QUERY).mutate<any>({
      mutation: QUERY,
      variables: { input },
      context: {
        headers: {
          'Content-type': 'application/json',
          'Authorization': 'Bearer ' + user.auth.token
        }
      }
    }).toPromise().then((response: MutationResult) => {
      return response;
    }).catch((error: ApolloError) => {
      return this.normalizeApolloError(error);
    });
  }


  // fetch Doctor Details
  async getQueryData(user, QUERY, input) {
    return await this.resolveClient(QUERY).query<any>({
      query: QUERY,
      variables: input,
      fetchPolicy: 'network-only',
      context: {
        headers: {
          'Content-type': 'application/json',
          'Authorization': 'Bearer ' + user.auth.token
        }
      }
    }).toPromise().then((response: ApolloQueryResult<any> | any) => {
      return response;
    }).catch((error: ApolloError) => {
      return this.normalizeApolloError(error);
    });
  }

  // fetch Organization Details
  async getQueryOrganizationData(user, QUERY, input) {
    try {
      const response = await this.resolveClient(QUERY).query<any>({
        query: QUERY,
        variables: input,
        fetchPolicy: 'network-only',
        context: {
          headers: {
            'Content-type': 'application/json',
            'Authorization': 'Bearer ' + user.auth.token
          }
        }
      }).toPromise();

      return response; // { data, errors }
    } catch (error: ApolloError | any) {
      return {
        data: null,
        errors: error.graphQLErrors || [{ message: error.message || 'Unknown error' }]
      };
    }
  }


  // update organization details
  // NOTE: sends `input`'s keys as flat top-level GraphQL variables (not
  // wrapped under a single `input` variable), matching org-service's
  // updateOrganization(orgId: ID!, input: [UpdateOpInput]!) flat-arg signature.
  async update_organization_details(user: any, QUERY: any, input: any) {
    return await this.resolveClient(QUERY).mutate<any>({
      mutation: QUERY,
      variables: { ...input },
      context: {
        headers: {
          'Content-type': 'application/json',
          'Authorization': 'Bearer ' + user.auth.token
        }
      }
    }).toPromise().then((response: MutationResult) => {
      return response;
    }).catch((error: ApolloError) => {
      return this.normalizeApolloError(error);
    });
  }

  // fetch query result GET
  executeQuery(user, QUERY, input, serviceOverride?: ServiceSlug) {

    let headers: any = { 'Content-type': 'application/json' };
    if (user) {
      headers = { ...headers, ['Authorization']: 'Bearer ' + user.auth.token + '' };
    }
    return this.resolveClient(QUERY, serviceOverride).watchQuery<ApolloQueryResult<any> | any>({
      query: QUERY,
      variables: input,
      fetchPolicy: 'network-only',
      context: {
        headers
      },
    }).valueChanges.pipe(catchError((err: ApolloError) => throwError(this.normalizeApolloError(err))));
  }

  // execute mutate query POST PATCH DELETE

  executeMutateQuery(user, QUERY, input, serviceOverride?: ServiceSlug) {
    let headers: any = { 'Content-type': 'application/json' };
    if (user) {
      headers = { ...headers, ['Content-type']: 'application/json', ['Authorization']: 'Bearer ' + user.auth.token + '' };
    }

    return this.resolveClient(QUERY, serviceOverride).mutate<MutationResult>({
      mutation: QUERY,
      variables: input,
      context: {
        headers
      },
    }).pipe(catchError((err: ApolloError) => throwError(this.normalizeApolloError(err))));
  }

  executeCacheQuery(user: any, QUERY: any, input: any = {}, options: { fetchPolicy?: 'cache-first' | 'network-only' | 'no-cache' } = { fetchPolicy: 'cache-first' }, serviceOverride?: ServiceSlug) {
    try {
      let headers: any = { 'Content-type': 'application/json' };
      if (user) {
        headers = { ...headers, ['Authorization']: 'Bearer ' + user.auth.token + '' };
      }
      const apolloOptions: any = {
        query: QUERY,
        variables: input,
        fetchPolicy: options.fetchPolicy || 'cache-first',
        context: {
          headers
        }
      };
      return this.resolveClient(QUERY, serviceOverride).query(apolloOptions).pipe(catchError((err: ApolloError) => throwError(this.normalizeApolloError(err))));
    } catch (err) {
      return throwError(err);
    }
  }
}
